/**
 * Admin-specific constants and configuration values
 */

// Import enums from the types library to avoid duplication
import {
  UserRole,
  UserStatus,
  BuyerStatus,
  SupplierStatus,
  OrderStatus,
  PaymentStatus,
  Priority,
  Currency,
  Language,
} from '@choco-admin/types';

// Create constant objects from enum values for backwards compatibility
export const USER_ROLES = {
  SUPER_ADMIN: UserRole.SUPER_ADMIN,
  ADMIN: UserRole.ADMIN,
  MANAGER: UserRole.MANAGER,
  SUPPORT: UserRole.SUPPORT,
  VIEWER: UserRole.VIEWER,
} as const;

export const USER_STATUSES = {
  ACTIVE: UserStatus.ACTIVE,
  INACTIVE: UserStatus.INACTIVE,
  PENDING: UserStatus.PENDING,
  SUSPENDED: UserStatus.SUSPENDED,
} as const;

export const BUYER_STATUSES = {
  ACTIVE: BuyerStatus.ACTIVE,
  PENDING_ACTIVATION: BuyerStatus.PENDING_ACTIVATION,
} as const;

export const SUPPLIER_STATUSES = {
  ACTIVE: SupplierStatus.ACTIVE,
  INACTIVE: SupplierStatus.INACTIVE,
  PENDING_APPROVAL: SupplierStatus.PENDING_APPROVAL,
  REJECTED: SupplierStatus.REJECTED,
  SUSPENDED: SupplierStatus.SUSPENDED,
  ONBOARDING: SupplierStatus.ONBOARDING,
} as const;

export const ORDER_STATUSES = {
  CREATED: OrderStatus.CREATED,
  PENDING_BUYER_APPROVAL: OrderStatus.PENDING_BUYER_APPROVAL,
  REJECTED_BY_BUYER: OrderStatus.REJECTED_BY_BUYER,
  PENDING: OrderStatus.PENDING,
  CONFIRMED: OrderStatus.CONFIRMED,
  DELIVERED: OrderStatus.DELIVERED,
} as const;

export const PAYMENT_STATUSES = {
  PENDING: PaymentStatus.PENDING,
  PROCESSING: PaymentStatus.PROCESSING,
  COMPLETED: PaymentStatus.COMPLETED,
  FAILED: PaymentStatus.FAILED,
  CANCELLED: PaymentStatus.CANCELLED,
  REFUNDED: PaymentStatus.REFUNDED,
} as const;

export const PRIORITY_LEVELS = {
  LOW: Priority.LOW,
  MEDIUM: Priority.MEDIUM,
  HIGH: Priority.HIGH,
  URGENT: Priority.URGENT,
} as const;

export const CURRENCIES = {
  USD: Currency.USD,
  EUR: Currency.EUR,
  GBP: Currency.GBP,
  CAD: Currency.CAD,
  AUD: Currency.AUD,
} as const;

export const LANGUAGES = {
  EN: Language.EN,
  ES: Language.ES,
  FR: Language.FR,
  DE: Language.DE,
  IT: Language.IT,
} as const;

/**
 * Default locale
 */
export const DEFAULT_LOCALE = 'en-US';

// Re-export types from the types library
export type {
  UserRole,
  UserStatus,
  BuyerStatus,
  SupplierStatus,
  OrderStatus,
  PaymentStatus,
  Currency,
  Language,
};
export type PriorityLevel = Priority;

/**
 * Date formats
 */
export const DATE_FORMATS = {
  SHORT: 'MM/dd/yyyy',
  /** Day-first, unambiguous to a European reader. Label columns "DD.MM.YYYY". */
  DAY_FIRST: 'dd.MM.yyyy',
  /**
   * Day-first with a named month, so it reads unambiguously without a format
   * label on the column. Prefer this in tables where the header has no room.
   */
  DAY_FIRST_MEDIUM: 'dd. MMM, yyyy',
  MEDIUM: 'MMM dd, yyyy',
  LONG: 'MMMM dd, yyyy',
  ISO: 'yyyy-MM-dd',
  DATETIME: 'MMM dd, yyyy HH:mm',
  TIME: 'HH:mm',
} as const;

/**
 * Pagination defaults
 */
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_PAGE_SIZE: 100,
} as const;

/**
 * File upload limits
 */
export const FILE_UPLOAD = {
  MAX_SIZE_MB: 10,
  MAX_SIZE_BYTES: 10 * 1024 * 1024,
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_DOCUMENT_TYPES: [
    'application/pdf',
    'text/csv',
    'application/vnd.ms-excel',
  ],
} as const;

/**
 * API endpoints (base paths)
 */
export const API_ENDPOINTS = {
  USERS: '/api/users',
  BUYERS: '/api/buyers',
  SUPPLIERS: '/api/suppliers',
  ORDERS: '/api/orders',
  PRODUCTS: '/api/products',
  PAYMENTS: '/api/payments',
  REPORTS: '/api/reports',
  SETTINGS: '/api/settings',
} as const;

/**
 * Local storage keys
 */
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'choco_admin_token',
  USER_PREFERENCES: 'choco_admin_preferences',
  SIDEBAR_COLLAPSED: 'choco_admin_sidebar_collapsed',
  TABLE_FILTERS: 'choco_admin_table_filters',
} as const;

/**
 * Validation limits
 */
export const VALIDATION_LIMITS = {
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 100,
  EMAIL_MAX_LENGTH: 255,
  PHONE_MIN_LENGTH: 10,
  PHONE_MAX_LENGTH: 15,
  PASSWORD_MIN_LENGTH: 8,
  ADDRESS_MAX_LENGTH: 255,
  DESCRIPTION_MAX_LENGTH: 1000,
} as const;

/**
 * Status badge colors mapping
 */
export const USER_STATUS_COLORS = {
  [USER_STATUSES.ACTIVE]: 'success',
  [USER_STATUSES.INACTIVE]: 'neutral',
  [USER_STATUSES.PENDING]: 'warning',
  [USER_STATUSES.SUSPENDED]: 'danger',
} as const;

export const BUYER_STATUS_COLORS = {
  [BUYER_STATUSES.ACTIVE]: 'success',
  // Pending activation is the normal state of a brand-new buyer and resolves
  // by itself, so it is not styled as a warning.
  [BUYER_STATUSES.PENDING_ACTIVATION]: 'neutral',
} as const;

export const ORDER_STATUS_COLORS = {
  [ORDER_STATUSES.CREATED]: 'warning',
  [ORDER_STATUSES.PENDING_BUYER_APPROVAL]: 'warning',
  [ORDER_STATUSES.REJECTED_BY_BUYER]: 'danger',
  [ORDER_STATUSES.PENDING]: 'warning',
  [ORDER_STATUSES.CONFIRMED]: 'info',
  [ORDER_STATUSES.DELIVERED]: 'success',
} as const;

// Helper function to get status color
export const getStatusColor = (
  status: string,
  type: 'user' | 'buyer' | 'order'
) => {
  switch (type) {
    case 'user':
      return (
        USER_STATUS_COLORS[status as keyof typeof USER_STATUS_COLORS] ||
        'neutral'
      );
    case 'buyer':
      return (
        BUYER_STATUS_COLORS[status as keyof typeof BUYER_STATUS_COLORS] ||
        'neutral'
      );
    case 'order':
      return (
        ORDER_STATUS_COLORS[status as keyof typeof ORDER_STATUS_COLORS] ||
        'neutral'
      );
    default:
      return 'neutral';
  }
};
